import { useState } from 'react'
import { catalogApi } from '../lib/api'
import type { Category, Product } from '../lib/db'
import { imageUrl } from '../lib/menuImages'
import { money } from '../lib/money'
import { scannerInputProps } from '../lib/scanner'
import { useApp } from '../lib/store'
import { Sheet } from './Sheet'
import { NumPad } from './NumPad'
import { ImagePicker } from './ImagePicker'

/** Đơn vị hay gặp. Gõ tay được, nhưng bấm một cái thì nhanh hơn. */
const UNITS = ['ly', 'chai', 'lon', 'ổ', 'dĩa', 'tô', 'phần', 'cái', 'gói', 'kg']

/** Bộ tuỳ chọn nhanh dựng sẵn — đúng một lớp, in thẳng lên mặt nút gọi món. */
const OPTION_SETS = [
  { label: 'Không có', value: '' },
  { label: 'Đá / Nóng', value: 'Đá,Nóng' },
  { label: 'M / L', value: 'M,L' },
  { label: 'Ít đường / Bình thường', value: 'Ít đường,Bình thường' },
]

export interface ProductDraft {
  id?: string
  categoryId: string | null
  name: string
  price: number
  unit: string
  quickOptions: string
  soldByWeight: boolean
  barcode: string
  imageKey: string | null
}

export function toDraft(p: Product): ProductDraft {
  return {
    id: p.id,
    categoryId: p.categoryId,
    name: p.name,
    price: p.price,
    unit: p.unit,
    quickOptions: p.quickOptions.join(','),
    soldByWeight: p.soldByWeight,
    barcode: p.barcode ?? '',
    imageKey: p.imageKey ?? null,
  }
}

export function blankDraft(categoryId: string | null, barcode = ''): ProductDraft {
  return {
    categoryId,
    name: '',
    price: 0,
    unit: 'ly',
    quickOptions: '',
    soldByWeight: false,
    barcode,
    imageKey: null,
  }
}

interface Props {
  draft: ProductDraft
  categories: Category[]
  onClose: () => void
  onSaved: () => void
  /**
   * `sheet` là tấm trượt che màn hình (bộ Classic). `pane` là nội dung trần để bộ Modern
   * nhét vào khoang bên phải. Chỉ khác cái khung — phần kiểm tra và lưu vẫn là một đoạn mã.
   */
  variant?: 'sheet' | 'pane'
}

export function ProductEditor({
  draft: initial,
  categories,
  onClose,
  onSaved,
  variant = 'sheet',
}: Props) {
  const say = useApp((s) => s.say)
  const [d, setD] = useState<ProductDraft>(initial)
  const [busy, setBusy] = useState(false)
  const [picking, setPicking] = useState(false)

  const patch = (p: Partial<ProductDraft>) => setD((c) => ({ ...c, ...p }))
  const isNew = !d.id

  async function save() {
    if (!d.name.trim()) {
      say('Chưa có tên món')
      return
    }
    if (d.price <= 0) {
      say('Chưa nhập giá bán')
      return
    }

    setBusy(true)
    try {
      await catalogApi.saveProduct({
        id: d.id,
        categoryId: d.categoryId,
        name: d.name.trim(),
        price: d.price,
        unit: d.unit,
        quickOptions: d.quickOptions || null,
        soldByWeight: d.soldByWeight,
        barcode: d.barcode.trim() || null,
        imageKey: d.imageKey,
      })
      say(isNew ? `Đã thêm ${d.name.trim()}` : 'Đã lưu')
      onSaved()
    } catch (e) {
      say(e instanceof Error ? e.message : 'Chưa lưu được')
    }
    setBusy(false)
  }

  async function remove() {
    if (!d.id) return
    setBusy(true)
    try {
      await catalogApi.deleteProduct(d.id)
      say(`Đã ẩn ${d.name}`)
      onSaved()
    } catch (e) {
      say(e instanceof Error ? e.message : 'Chưa xoá được')
    }
    setBusy(false)
  }

  const title = isNew ? 'Thêm món' : d.name || 'Sửa món'

  const body = (
    <>
      {/*
        Ô ảnh đứng trước ô tên, và đó là cả ý đồ: bấm một tấm trong kho là tên với đơn vị
        điền luôn. Đường nhanh nhất để thêm một món là bấm ảnh rồi gõ giá.
      */}
      <div className="field">
        <span>Ảnh món</span>
        <button type="button" className="pick-photo" onClick={() => setPicking(true)}>
          {d.imageKey ? (
            <img src={imageUrl(d.imageKey) ?? ''} alt="" />
          ) : (
            <span className="pick-photo-empty" aria-hidden="true">
              📷
            </span>
          )}
          <span className="pick-photo-text">
            <b>{d.imageKey ? 'Đổi ảnh khác' : 'Chọn ảnh có sẵn'}</b>
            <small>
              {d.imageKey
                ? 'Bấm để chọn tấm khác hoặc bỏ ảnh'
                : 'Bấm một tấm là điền sẵn tên và đơn vị'}
            </small>
          </span>
          <span aria-hidden="true">›</span>
        </button>
      </div>

      <label className="field">
        <span>Tên món</span>
        <input
          value={d.name}
          onChange={(e) => patch({ name: e.target.value })}
          placeholder="Cà phê sữa đá"
          maxLength={120}
          autoFocus={isNew}
        />
      </label>

      <div className="field">
        <span>Giá bán</span>
        <div className="amount-readout num">{d.price ? money(d.price) : '0'}</div>
        <div className="chip-row">
          {[10_000, 15_000, 20_000, 25_000, 30_000, 35_000].map((v) => (
            <button
              key={v}
              type="button"
              className="chip"
              aria-pressed={d.price === v}
              onClick={() => patch({ price: v })}
            >
              {money(v)}
            </button>
          ))}
        </div>
        <NumPad value={d.price} onChange={(price) => patch({ price })} />
      </div>

      <div className="field-row">
        <label className="field">
          <span>Nhóm</span>
          <select
            value={d.categoryId ?? ''}
            onChange={(e) => patch({ categoryId: e.target.value || null })}
          >
            <option value="">— Chưa xếp nhóm —</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span>Đơn vị</span>
          <select value={d.unit} onChange={(e) => patch({ unit: e.target.value })}>
            {UNITS.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="field">
        <span>Tuỳ chọn nhanh</span>
        <select value={d.quickOptions} onChange={(e) => patch({ quickOptions: e.target.value })}>
          {OPTION_SETS.map((o) => (
            <option key={o.label} value={o.value}>
              {o.label}
            </option>
          ))}
          {/* Bộ tuỳ chọn cũ không nằm trong danh sách dựng sẵn thì vẫn phải giữ lại. */}
          {d.quickOptions && !OPTION_SETS.some((o) => o.value === d.quickOptions) && (
            <option value={d.quickOptions}>{d.quickOptions.replace(/,/g, ' / ')}</option>
          )}
        </select>
      </label>

      <label className="field">
        <span>Mã vạch</span>
        <input
          value={d.barcode}
          onChange={(e) => patch({ barcode: e.target.value })}
          placeholder="Quét mã hoặc gõ tay"
          inputMode="numeric"
          maxLength={64}
          {...scannerInputProps((code) => patch({ barcode: code }))}
        />
      </label>
      <p className="note">
        Đặt con trỏ vào ô trên rồi quét — máy scan tự điền. Món tự pha như cà phê thì để trống.
      </p>

      <label className="row-btn">
        <span className="row-icon" aria-hidden="true">
          ⚖️
        </span>
        <span className="row-text">
          <b>Bán theo cân</b>
          <small>Thịt, cá, rau — nhập số ký thay vì bấm cộng</small>
        </span>
        <input
          type="checkbox"
          checked={d.soldByWeight}
          onChange={(e) => patch({ soldByWeight: e.target.checked, unit: e.target.checked ? 'kg' : d.unit })}
        />
      </label>

      <div className="sheet-actions">
        {!isNew && (
          <button type="button" className="btn btn-danger-ghost" onClick={remove} disabled={busy}>
            Xoá
          </button>
        )}
        <button type="button" className="btn btn-brand grow" onClick={save} disabled={busy}>
          {busy ? 'Đang lưu…' : isNew ? 'Thêm món' : 'Lưu'}
        </button>
      </div>
    </>
  )

  const picker = picking ? (
    <ImagePicker
      value={d.imageKey}
      onClose={() => setPicking(false)}
      onPick={(imageKey, suggest) =>
        // Tên và đơn vị chỉ điền khi ô tên còn trống — không đè lên thứ người ta đã gõ.
        patch({
          imageKey,
          ...(suggest && !d.name.trim() ? { name: suggest.name, unit: suggest.unit } : {}),
        })
      }
    />
  ) : null

  if (variant === 'sheet') {
    return (
      <>
        <Sheet title={title} onClose={onClose}>
          {body}
        </Sheet>
        {picker}
      </>
    )
  }

  return (
    <div className="detail-pane">
      <div className="pane-head">
        <span className="pane-name">{title}</span>
        <span className="pane-seats">{d.price > 0 ? `${money(d.price)}đ` : 'Chưa có giá'}</span>
        <button type="button" className="icon-btn" onClick={onClose} aria-label="Đóng">
          ✕
        </button>
      </div>
      <div className="detail-body">{body}</div>
      {picker}
    </div>
  )
}
